//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { PurchaseOption } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { isSingleRetailerPurchaseOption } from '@campaigns/utils/sampling/PrepaidCampaignUtils';
import { uniq } from '@utils/arrayUtils';
import { isUndefined } from '@utils/filterPredicates';
import { toGrammaticallyCorrectList } from '@utils/stringUtils';

export const getCompatibilityErrorMessage = (
  options: PurchaseOption[],
): string | undefined => {
  if (options.length <= 1) {
    return undefined;
  }

  const contentTypes = uniq(options.map((opt) => opt.contentType));
  const postSurveyValues = uniq(
    options.map((opt) => opt.hasPostSurvey ?? false),
  );
  const hasContentTypeIssue =
    contentTypes.length > 1 || postSurveyValues.length > 1;

  const deliveryMethods = uniq(options.map((opt) => opt.deliveryMethod));
  const allInserts = uniq(
    options.flatMap((opt) =>
      (opt.fulfillmentOptions ?? []).map((fo) => fo.insert),
    ),
  );
  const allCartonTypes = uniq(
    options.flatMap((opt) =>
      (opt.fulfillmentOptions ?? []).map((fo) => fo.cartonType),
    ),
  );
  const hasMultipleFulfillmentOptions = options.some(
    (opt) => (opt.fulfillmentOptions ?? []).length > 1,
  );
  // hasMultipleFulfillmentOptions above ensures that when multiple POs are
  // selected, each PO has at most one fulfillmentOptions entry. We therefore
  // only need to compare the first entry per PO rather than aggregating across
  // all entries, which avoids false positives from within-PO country variation.
  const allCountryCodes = uniq(
    options.map((opt) => {
      const fo = (opt.fulfillmentOptions ?? [])[0];
      return [...(fo?.availableCountryCodes ?? [])].sort().join(',');
    }),
  );
  const hasDeliveryMethodIssue =
    deliveryMethods.length > 1 ||
    allInserts.length > 1 ||
    allCartonTypes.length > 1 ||
    hasMultipleFulfillmentOptions ||
    allCountryCodes.length > 1;

  const usageTypes = uniq(options.map((opt) => opt.areUnitsFlexible));
  const singleRetailerFlags = uniq(
    options.map((opt) => isSingleRetailerPurchaseOption(opt)),
  );
  const hasUsageTypeIssue =
    usageTypes.length > 1 || singleRetailerFlags.length > 1;

  const attributeChecks: { hasIssue: boolean; label: string }[] = [
    { hasIssue: hasContentTypeIssue, label: 'Content type' },
    { hasIssue: hasDeliveryMethodIssue, label: 'Delivery method' },
    { hasIssue: hasUsageTypeIssue, label: 'Usage type' },
  ];

  const mismatchedAttributes = attributeChecks
    .filter(({ hasIssue }) => hasIssue)
    .map(({ label }) => label);

  if (mismatchedAttributes.length === 0) {
    return undefined;
  }

  const attributeList = toGrammaticallyCorrectList(mismatchedAttributes);

  return `The selected purchase options do not have matching attributes. ${attributeList}.`;
};

export const arePurchaseOptionsCompatible = (
  options: PurchaseOption[],
): boolean => isUndefined(getCompatibilityErrorMessage(options));
